import { useState, useEffect } from 'react';
import { NewsItem } from '../types/news';
import { fetchNews } from '../services/newsService';

export const useNewsFeed = () => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [showFeed, setShowFeed] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const [lastViewedNewsId, setLastViewedNewsId] = useState<string | null>(() =>
    localStorage.getItem('lastViewedNewsId'),
  );

  useEffect(() => {
    const updateNews = async () => {
      const sortedItems = await fetchNews();
      setNewsItems(sortedItems);

      const mostRecentNewsId = sortedItems[0]?.id;
      setHasNewNotification(mostRecentNewsId ? mostRecentNewsId !== lastViewedNewsId : false);
    };

    updateNews();

    // Refetch when the operator returns to the tab instead of polling on a blind
    // interval. fetchNews() is throttled by its own localStorage cache and
    // de-duplicates concurrent calls, so returning to the panel triggers at most
    // one network request — and only once the cache has expired. A newly
    // published article surfaces the next time the operator is active.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        updateNews();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [lastViewedNewsId]);

  const handleNotificationClick = () => {
    setShowFeed(!showFeed);

    if (!showFeed) {
      const mostRecentNewsId = newsItems[0]?.id;
      if (mostRecentNewsId) {
        setLastViewedNewsId(mostRecentNewsId);
        localStorage.setItem('lastViewedNewsId', mostRecentNewsId);
        setHasNewNotification(false);
      }
    }
  };

  const handleArticleClick = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleCloseFeed = () => {
    setShowFeed(false);
  };

  return {
    newsItems,
    showFeed,
    hasNewNotification,
    handleNotificationClick,
    handleArticleClick,
    handleCloseFeed,
  };
};
